/**
 * The breadcrumb, tier chip, inspector and sampler cards, the canvas
 * badges, and the corridor view a class (c) body's cloud-top bands mount
 * into: everything `renderOverlay` paints over the scene each time state
 * changes.
 *
 * Reads the closure it was cut out of through `OverlayViewDeps`. `notInSlice`
 * and `overlayDirty` live in `state`; root code outside this seam (refuseBody,
 * refuseSite, createInspector, createInput, createAtlasFrame) reaches them
 * through the same getter/setter pair as `corridorView`.
 */

import { Quaternion, Vector3 } from 'three';

import { getClaim } from './claims.ts';
import {
  corridorBandIndexAt, createCorridorView, type CorridorViewHandle,
} from './corridor.ts';
import type { BodyReadersHandle } from './bodyReaders';
import type { BodySceneHandle } from './bodyScene';
import type { CameraRigHandle } from './cameraRig';
import type { DescentAnchorHandle } from './descentAnchor';
import type { FocusRigHandle } from './focusRig';
import type { HexViewHandle } from './hexView';
import { escapeHtml } from '../html.ts';
import type { InjectsHandle } from './injects';
import type { InspectorHandle } from './inspector';
import type { BodyManifestHandle } from './manifestPanel';
import type { MountHandle } from './mount';
import type { NavigationHandle } from './navigation';
import type { PlotControlsHandle } from './panels/plotControls';
import { isDaytime, type PlotClimate } from './plotField.ts';
import { plotAddress, parcelsIn, parcelAreaKm2 } from './plotName.ts';
import { isPyramidPlot } from './pyramidPlot.ts';
import type { PyramidViewHandle } from './pyramidView';
import type { SiteChipsHandle } from './panels/siteChips';
import type { SiteGroundHandle } from './siteGround';
import type { SolarScrubHandle } from './solarScrub';
import type { SurfaceRadiusHandle } from './surfaceRadius';
import type { ViewClockHandle } from './viewClock';
import type { AtlasLive, AtlasState } from '../atlas';

// The reference footprint drawn on a selected cell: the array area that
// delivers this much mean electrical power from THAT cell's own insolation.
export const REFERENCE_MW = 1;
/** 1 MW held across one of THIS body's sols, which is what sizes the array. */
export const referenceWhSol = (climate: PlotClimate): number =>
  REFERENCE_MW * 1e6 * (climate.solLengthS / 3600);

export interface OverlayViewDeps {
  readonly bodyReaders: BodyReadersHandle;
  readonly bodyScene: BodySceneHandle;
  readonly cameraRig: CameraRigHandle;
  readonly descentAnchorHandle: DescentAnchorHandle;
  readonly focusRig: FocusRigHandle;
  readonly hexViewSeam: HexViewHandle;
  readonly injects: InjectsHandle;
  readonly live: AtlasLive;
  readonly mount: MountHandle;
  readonly navigation: NavigationHandle;
  readonly plotControls: PlotControlsHandle;
  readonly siteGround: SiteGroundHandle;
  readonly siteChips: SiteChipsHandle;
  readonly surfaceRadius: SurfaceRadiusHandle;
  readonly viewClockHandle: ViewClockHandle;

  readonly elBadge: HTMLElement;
  readonly elBreadcrumb: HTMLElement;
  readonly elHero: HTMLElement;
  readonly elInspector: HTMLElement;
  readonly elSampler: HTMLElement;
  /** The scrolling content div under the ground panel's own fixed header,
   *  the one `renderPlotSampler` rewrites; `elSampler` stays put so its
   *  header and minimize button never get torn out from under a listener. */
  readonly elSamplerBody: HTMLElement;
  readonly elTier: HTMLElement;
  readonly manifest: BodyManifestHandle;
  readonly pyramidView: PyramidViewHandle;
  readonly solarScrub: SolarScrubHandle;
  readonly state: AtlasState;

  /** State the closure owns and this seam only reads. Getters, so a
   *  reassignment on the other side is seen rather than snapshotted here. */
  readonly addressesReady: boolean;
  readonly ensureCardAddresses: () => void;
  readonly inspector: InspectorHandle;

  /** Read and written from inside this seam. */
  badgeHtml: string | null;
}

export interface OverlayViewState {
  readonly corridorView: { id: string; lifted: boolean; handle: CorridorViewHandle } | null;
  notInSlice: string | null;
  overlayDirty: boolean;
}

export interface OverlayViewHandle {
  updateCorridor(): void;
  syncBadge(): void;
  renderOverlay(): void;
  dispose(): void;
  readonly state: OverlayViewState;
}

export function createOverlayView(deps: OverlayViewDeps): OverlayViewHandle {
  const {
    elBadge, elBreadcrumb, elHero, elInspector, elSampler, elSamplerBody, elTier, manifest, pyramidView, solarScrub,
    state,
  } = deps;
  const { bodyClimate, bodyQuads, corridorDebug, corridorFor, descendable, quadFor, rowById } = deps.bodyReaders;
  const { formatAltitude, formatSample, shipPlace, surfaceRegime } = deps.cameraRig;
  const { availableQuad, displaySite } = deps.descentAnchorHandle;
  const { altitude, band, shipFocused } = deps.focusRig;
  const { cellCrumb, hexOverlay } = deps.hexViewSeam;
  const { DAY_BADGE_EVERY_FRAME } = deps.injects.state;
  const { camera, cameraWorld } = deps.mount;
  const { zoomOut } = deps.navigation;
  const { elPlotCtl, pcLs, pcLsRow, pcPlayYear, setPlaying, syncSolNote } = deps.plotControls;
  const { parentOf } = deps.siteGround;
  const { solar, solsSinceAnchor } = solarScrub;
  const { groundDatumM } = deps.surfaceRadius;

  /** The not-in-slice card, held until the next pick or Escape. */
  let notInSlice: string | null = null;

  /** A wheel notch repaints the card, but once a frame and not once an event. */
  let overlayDirty = false;

  /**
   * The cloud-top corridor view, on the focused class (c) body and nowhere else.
   *
   * Built lazily and parented under the body's own sphere, so the pole
   * quaternion, the camera-relative frame and the angular-floor scale are the
   * body's - the same place `rings.ts` hangs. Freed the moment the focus leaves
   * the body or the Atlas closes, which is what `leak-check` reads.
   */
  let corridorView: { id: string; lifted: boolean; handle: CorridorViewHandle } | null = null;
  /** The band the breadcrumb and the chip were last written for. */
  let corridorBandDrawn: number | null = null;
  const releaseCorridorView = (): void => {
    corridorView?.handle.dispose();
    corridorView = null;
  };
  const _corrQ = new Quaternion();
  const _corrEye = new Vector3();
  const _corrDir = new Vector3();

  /**
   * Mount, aim and light the corridor view for the focused class (c) body.
   *
   * Visible only inside the corridor's own top edge: above it the three shells
   * are under a pixel of a 70,000 km disc and the globe is what a person is
   * looking at, and the reach row says the same thing at every altitude either
   * way.
   */
  function updateCorridor(): void {
    const geo = corridorFor(state.bodyId);
    const node = state.bodyId === null || geo === null
      ? null
      : deps.bodyScene.state.orrery?.byId(state.bodyId) ?? null;
    if (geo === null || !node) {
      releaseCorridorView();
      corridorBandDrawn = null;
      return;
    }
    const lifted = corridorDebug.liftLowestBottom;
    if (corridorView && (corridorView.id !== node.row.id || corridorView.lifted !== lifted)) {
      releaseCorridorView();
    }
    if (!corridorView) {
      const handle = createCorridorView({ row: node.row, geometry: geo });
      node.mesh.add(handle.group);
      corridorView = { id: node.row.id, lifted, handle };
    }
    // The eye into the body-fixed frame and into the geometry's own metres, the
    // way `rings.update` takes it: the sphere and everything parented to it are
    // drawn at the angular floor's scale, and a direction taken against a
    // scaled position is not the direction the fragment looks along.
    _corrQ.copy(node.quaternion).invert();
    _corrEye
      .set(cameraWorld.x - node.world.x, cameraWorld.y - node.world.y,
        cameraWorld.z - node.world.z)
      .applyQuaternion(_corrQ)
      .divideScalar(Math.max(1e-9, node.mesh.scale.x));
    // The look direction in the same body-fixed frame. The three.js camera sits
    // at the scene origin, so its world forward IS the view direction.
    camera.getWorldDirection(_corrDir).applyQuaternion(_corrQ);
    const alt = altitude();
    corridorView.handle.update(_corrEye, _corrDir, alt);
    corridorView.handle.setVisible(alt <= geo.topAltitudeM);
    // The overlay is written on state changes and the three corridor bands sit
    // INSIDE one ladder rung, so crossing a band edge changes no tier and would
    // leave the breadcrumb and the chip naming the band the camera left.
    const bandNow = corridorBandIndexAt(geo, alt);
    if (bandNow !== corridorBandDrawn) {
      corridorBandDrawn = bandNow;
      overlayDirty = true;
      deps.live.needsRender = true;
    }
  }

  /** Canvas badges: relief exaggeration, preview mode, forecast scrub, day or night. */
  function syncBadge(): void {
    const badges: string[] = [];
    if (state.tier >= 5 && solar.exag !== 1.0) badges.push(`RELIEF &times;${solar.exag.toFixed(1)}`);
    if (state.tier >= 5 && (deps.viewClockHandle.state.playing === 'sol' || deps.viewClockHandle.state.solScrubbing)) badges.push(`PREVIEW &middot; sol`);
    if (state.tier >= 5 && deps.viewClockHandle.state.playing === 'year') badges.push(`FORECAST &middot; Ls ${solar.lsDeg.toFixed(0)}`);
    if (state.tier >= 5 && deps.viewClockHandle.state.forecasting) badges.push(`FORECAST &middot; Ls ${solar.lsDeg.toFixed(0)}`);
    if (state.tier >= 5 && deps.viewClockHandle.state.clockRunning && !deps.viewClockHandle.state.playing && !deps.viewClockHandle.state.forecasting) {
      // The site's latitude with a plot loaded, the ladder's own aim without
      // one: the same chain `plotSun` reads, so a siteless body says which half
      // of its day it is in rather than saying nothing.
      const lat = deps.live.plotField ? deps.live.plotField.meta.center_lat_deg : (solarScrub.solarLatLon()?.lat ?? null);
      const climate = bodyClimate();
      if (climate && lat !== null) {
        badges.push(isDaytime(solar.solPhase, lat, solar.lsDeg, climate, solarScrub.sunDeclinationDeg(climate)) ? 'DAY' : 'NIGHT');
      }
    }
    const html = badges.join(' &nbsp;|&nbsp; ');
    if (html === deps.badgeHtml && !DAY_BADGE_EVERY_FRAME) return;
    deps.badgeHtml = html;
    elBadge.innerHTML = html;
    elBadge.classList.toggle('hidden', badges.length === 0);
  }

  function renderOverlay(): void {
    manifest.update();
    deps.siteChips.syncFinder();
    // Breadcrumb
    const segs: string[] = [];
    segs.push(deps.inspector.crumb('Sol', state.tier === 0, () => zoomOut()));
    if (state.bodyId) {
      const b = rowById(state.bodyId);
      segs.push(deps.inspector.crumb(b?.display_name ?? state.bodyId, state.tier === 1));
    }
    if (state.quadId || (state.tier >= 2 && bodyQuads().length > 0)) {
      // Below the claim the quadrangle comes from the ground's OWN coordinates,
      // not from whichever quad was clicked three tiers ago on a synthetic hex
      // ring — which is how this used to read "MC-09 Tharsis" over Jezero.
      const real =
        state.tier >= 5 && deps.live.plotField
          ? quadFor(deps.live.plotField.meta.center_lat_deg, deps.live.plotField.meta.center_lon_deg)
          : undefined;
      const q = real
        ?? (state.quadId ? bodyQuads().find((x) => x.id === state.quadId) : availableQuad());
      if (q) segs.push(deps.inspector.crumb(`${q.id} ${q.name}`, state.tier === 2));
    }
    // Only on a body that HAS hexes. `loadState` runs before the registry does,
    // so a `?atlasTier=6` URL sets a cell id on every body, and a class (b) or
    // (c) floor that clamps the camera to tier 3 keeps it - which is how a Mars
    // hex slug reached Europa's breadcrumb and then Jupiter's.
    if (state.cellId && descendable()) {
      segs.push(deps.inspector.crumb(cellCrumb(state.cellId), state.tier === 3));
    }
    segs.push(...deps.inspector.corridorCrumbs());
    // Below the corridor there is no rung to arrive at, so the tier the floor
    // happens to read is not a reason to draw one as reached: Neptune's floor
    // is 45,000 m, which is tier 4, and Survey drew as an ordinary rung beside
    // the active corridor band on a body that can never have one.
    const noRungsBelow = corridorFor(state.bodyId) !== null;
    if (!noRungsBelow && state.tier >= 4) {
      segs.push(deps.inspector.crumb('Survey', state.tier === 4));
    } else {
      segs.push(deps.inspector.ghostCrumb('Survey', 4));
    }
    if (!noRungsBelow && state.tier >= 5) {
      segs.push(deps.inspector.crumb(displaySite()?.name ?? 'Region', state.tier === 5));
    } else {
      segs.push(deps.inspector.ghostCrumb('Region', 5));
    }
    if (!noRungsBelow && state.tier >= 6 && state.tileId) {
      const par = deps.live.selectedSub ? parentOf(deps.live.selectedSub) : null;
      segs.push(deps.inspector.crumb(par?.addr.name ?? `Plot ${state.tileId.replace('_', ', ')}`, true));
    } else {
      segs.push(deps.inspector.ghostCrumb('Plot', 6));
    }
    segs.push(deps.inspector.ghostCrumb('Walkaround', 6));
    elBreadcrumb.innerHTML = segs.join('<span class="atlas-bc-sep">›</span>');

    const bandNames = ['SYSTEM', 'BODY', 'QUADRANGLE', 'ORBITAL', 'SURVEY', 'REGION', 'PLOT'];
    // SURFACE shares tier 6 with PLOT (camera.ts's `tierAt`), so the chip has to
    // read the actual band rather than the tier to say which one it is.
    // A class (c) body's chip is its corridor band: "ORBITAL" is the rung the
    // floor happens to land on and says nothing about where in the corridor the
    // camera is.
    const corridorHere = corridorFor(state.bodyId);
    const corridorBand = corridorHere === null
      ? null
      : corridorBandIndexAt(corridorHere, altitude());
    elTier.textContent = corridorBand !== null
      ? corridorHere!.bands[corridorBand]!.name
      : surfaceRegime() ? 'SURFACE' : `${bandNames[state.tier]}`;

    // Hero title: shows at T0 only (paints big in the center on first land).
    elHero.classList.toggle('show', state.tier === 0);
    // The panel itself shows at PLOT and SURFACE on EVERY body. It used to need
    // a loaded plot field as well, and a body with no `sites.json` row never
    // gets one, so the clock and the sun were unreachable on Earth, Mercury and
    // every other siteless row - where the ground under the camera is the
    // pyramid's own leaves and is worth standing on. Which ROWS show is two
    // classes, one per set, resolved by the CSS at the foot of this file:
    // `.plot-only` for the rows that act on a plot field or a site,
    // `.sol-only` for the SOL scrubber and its play button, which need a solar
    // day the body's own row can answer for.
    //
    // A siteless body's own ground still gets a `plotField` - `isPyramidPlot`
    // ("built here rather than loaded from a bake") is what tells that field
    // apart from a real survey plot, so Extractor, Refinery, CELLS, the site
    // list and the rest of `.plot-only` stay hidden on Titan and every other
    // body with no site: there is nowhere on them a plot can be claimed.
    const solDayS = solarScrub.bodySolarDayS();
    const hasRealPlot = deps.live.plotField !== null && !isPyramidPlot(deps.live.plotField.meta);
    elPlotCtl.classList.toggle(
      'hidden', state.tier < 5 || (solarScrub.plotCtlDebug.plotFieldGate && !deps.live.plotField));
    elPlotCtl.classList.toggle('no-plot', !hasRealPlot);
    elPlotCtl.classList.toggle('no-sol', solDayS === null);
    syncSolNote(solDayS);
    // No date moves a seasonless body's Ls, so SEASON and YEAR could only move the clock.
    const seasonMoves = solarScrub.seasonMoves();
    const noSeason = seasonMoves ? '' : `${rowById(state.bodyId)?.display_name ?? 'This body'} has no seasons `
      + 'on record, so its sun stays over the equator at the same strength all year and there is no season to move to.';
    pcLs.disabled = !seasonMoves;
    pcPlayYear.disabled = !seasonMoves;
    pcLsRow.title = noSeason;
    pcPlayYear.title = noSeason;
    if (!seasonMoves && deps.viewClockHandle.state.playing === 'year') setPlaying(null);
    syncBadge();

    // Inspector
    if (notInSlice) {
      elInspector.innerHTML = notInSlice;
    } else if (shipFocused()) {
      const place = shipPlace();
      elInspector.innerHTML = `
        <div class="atlas-card">
          <div class="atlas-card-row"><span>focus</span><span>your ship</span></div>
          <div class="atlas-card-row"><span>from Sol</span><span>${
  place === null ? '&mdash;' : formatAltitude(place.solM)}</span></div>
          <div class="atlas-card-row"><span>nearest</span><span>${
  place === null || place.nearId === null ? '&mdash;'
    : `${rowById(place.nearId)?.display_name ?? place.nearId} · ${formatAltitude(place.nearM)}`
}</span></div>
          <div class="atlas-card-hint">Drag to orbit the ship · scroll to zoom · Escape for the system.</div>
        </div>
      `;
    } else if (state.tier === 0 || !state.bodyId) {
      // T0: hero overlay carries the title — keep the inspector small.
      elInspector.innerHTML = `
        <div class="atlas-card">
          <div class="atlas-card-row"><span>bodies</span><span>${deps.bodyScene.state.bodyRows.length} catalogued</span></div>
          <div class="atlas-card-row"><span>navigable</span><span>${deps.bodyScene.state.bodyRows.filter((b) => b.availability.descend).length}</span></div>
          <div class="atlas-card-hint">Drag the void · scroll to zoom · pick a body from BODIES.</div>
        </div>
      `;
    } else {
      const body = rowById(state.bodyId);
      if (body) {
        elInspector.innerHTML = `
          <div class="atlas-card">
            <div class="atlas-card-title">${escapeHtml(body.display_name.toUpperCase())}</div>
            <div class="atlas-card-row"><span>class</span><span>${escapeHtml(body.class.replace(/^\d_/, ''))}</span></div>
            <div class="atlas-card-row"><span>radius</span><span>${(body.radius_m / 1000).toFixed(0)} km</span></div>
            <div class="atlas-card-row"><span>altitude</span><span>${formatAltitude(altitude())}</span></div>
            <div class="atlas-card-row"><span>band</span><span>${band()}</span></div>
            <div class="atlas-card-row"><span>ground sample</span><span>${formatSample()}</span></div>
            ${deps.inspector.ringGainNote(body) === null ? ''
              : `<div class="atlas-card-row" data-fold-anchor><span>rings</span><span id="body-ring-gain">${
                deps.inspector.ringGainNote(body)?.short}</span></div>`}
            ${state.tier < 5 ? deps.inspector.renderHonestyRows() : ''}
            <div class="atlas-card-hint">${deps.inspector.tierHint()}</div>
          </div>
        `;
      }
    }
    deps.inspector.snapInspectorFold();

    // Sampler — T3/T4 hex inspector OR T5 DTM detail
    if ((state.tier === 3 || state.tier === 4) && state.cellId) {
      const centre = hexOverlay.cellCentre(state.cellId);
      const size = hexOverlay.cellSize();
      if (centre && size) {
        const isAnchor = state.cellId === hexOverlay.anchorCell();
        const t3AnchorHint = 'Click the highlighted cell to drop into the Survey.';
        const t4AnchorHint = 'Click the highlighted cell again to descend into the terrain.';
        const otherHint = 'Yield Engine couples here in M4. Sampler stub shown.';
        const hint = isAnchor
          ? (state.tier === 3 ? t3AnchorHint : t4AnchorHint)
          : otherHint;
        const cellQuad = quadFor(centre.lat, centre.lon);
        // The ground under the cell centre, or nothing: the ring this replaced
        // invented 7,400 m on a body with a global DEM.
        const h = pyramidView.tileHeightAt(centre.lat, centre.lon);
        const drawn = hexOverlay.debug().drawnRes;
        const radiusM = groundDatumM(state.bodyId);
        // h3-js loads with the tier, not before, so the seat name and the
        // claim below it (both need it) read "\u2026" for one render at most.
        if (!deps.addressesReady) deps.ensureCardAddresses();
        const seat = deps.addressesReady ? plotAddress(centre.lat, centre.lon) : null;
        const claim = deps.addressesReady ? getClaim(centre.lat, centre.lon) : null;
        const parcels = deps.addressesReady ? parcelsIn(size.areaKm2, radiusM) : null;
        // `--inject card-static` reproduces the fiction this card used to
        // show always, on purpose: a body/quad that never changes regardless
        // of the picked cell is exactly the regression `--hex --card` exists
        // to catch.
        const bodyText = deps.injects.state.cardStaticDebug
          ? 'Mars'
          : (rowById(state.bodyId)?.display_name ?? state.bodyId ?? '\u2014');
        const quadText = deps.injects.state.cardStaticDebug
          ? 'MC-09 Tharsis'
          : (cellQuad ? `${cellQuad.id} ${cellQuad.name}` : '\u2014');
        // Sol is the Atlas's own mission clock, real either way; era never
        // appears because there is no era clock anywhere in the engine to
        // read one from, and inventing one is the fiction this replaces.
        const statusText = !deps.addressesReady
          ? '\u2026'
          : claim
            ? `<span class="atlas-held">HELD</span> &middot; ${escapeHtml(claim.holder)} &middot; Sol ${claim.sol}`
            : `<span class="atlas-dim">Unclaimed</span> &middot; Sol ${Math.round(solsSinceAnchor())}`;
        elSamplerBody.innerHTML = `
          <div class="atlas-card sampler ${isAnchor ? 'canon' : ''}">
            <div class="atlas-card-title">${escapeHtml(state.cellId.slice(0, 8))}</div>
            ${isAnchor ? `<div class="atlas-card-canon">SURVEY CELL, ${escapeHtml((displaySite()?.name ?? 'Surveyed Site').toUpperCase())} · canon parity</div>` : ''}
            <div class="atlas-card-row"><span>body</span><span>${escapeHtml(bodyText)}</span></div>
            <div class="atlas-card-row"><span>quad</span><span>${escapeHtml(quadText)}</span></div>
            <div class="atlas-card-row"><span>H3 res</span><span>${drawn ?? '\u2014'} · ${size.areaKm2.toFixed(2)} km² · ${(size.acrossFlatsM / 1000).toFixed(2)} km across</span></div>
            <div class="atlas-card-row"><span>lat / lon</span><span>${centre.lat.toFixed(2)}° / ${centre.lon.toFixed(2)}°</span></div>
            <div class="atlas-card-row"><span>elevation</span><span>${h === null ? 'not measured here' : `${Math.round(h).toLocaleString()} m`}</span></div>
            <div class="atlas-card-row"><span>seat parcel</span><span>${seat ? escapeHtml(seat.name) : '\u2026'}</span></div>
            <div class="atlas-card-row"><span>parcels</span><span>${parcels === null ? '\u2026' : `${parcels} &times; ${parcelAreaKm2(radiusM).toFixed(2)} km&sup2;`}</span></div>
            <div class="atlas-card-row"><span>status</span><span>${statusText}</span></div>
            <div class="atlas-card-hint">${hint}</div>
          </div>
        `;
        elSampler.classList.add('show');
      }
    } else if (state.tier >= 5) {
      // The panel rewrites itself on every survey; without this the reader is
      // yanked back to the top several times a second during a season sweep.
      const keepScroll = elSamplerBody.scrollTop;
      elSamplerBody.innerHTML = deps.inspector.renderPlotSampler();
      elSamplerBody.scrollTop = keepScroll;
      elSampler.classList.add('show');
    } else {
      elSampler.classList.remove('show');
      elSamplerBody.innerHTML = '';
    }
  }

  return {
    updateCorridor,
    syncBadge,
    renderOverlay,
    dispose: () => releaseCorridorView(),
    state: {
      get corridorView() { return corridorView; },
      get notInSlice() { return notInSlice; },
      set notInSlice(v) { notInSlice = v; },
      get overlayDirty() { return overlayDirty; },
      set overlayDirty(v) { overlayDirty = v; },
    },
  };
}
